import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
// import { Badge } from "../components/ui/badge"
import { Link } from "@inertiajs/react"
import { Code, Zap, Globe, FileText, CheckCircle, ArrowRight, Fingerprint, Bot } from "lucide-react"
import { ReactNode } from "react"
import Layout from "../layouts/Layout"
import { Head } from "@inertiajs/react"

const Services = () => {
  const services = [
    {
      icon: Code,
      title: "Ruby on Rails Development",
      description: "Full-stack Rails applications built with modern best practices, testing, and deployment strategies.",
      features: [
        "Custom Rails application development",
        "API development with RESTful and GraphQL",
        "Database design and optimization",
        "Third-party integrations",
        "Performance optimization",
        "Legacy system modernization"
      ],
      pricing: "Starting at $100/hour",
      timeline: "2-12 weeks depending on complexity"
    },
    {
      icon: Zap,
      title: "AI & Machine Learning Integration",
      description: "Prototype and implement AI-powered features to enhance your applications with intelligent capabilities.",
      features: [
        "LLM integration across providers (OpenAI, Anthropic, OpenRouter)",
        "MCP servers for tool-augmented agents",
        "Agent SDK applications with tool use and multi-step workflows",
        "Model selection and cost/performance benchmarking",
        "Natural language processing",
        "Data analysis and insights",
        "AI-powered automation"
      ],
      pricing: "Starting at $120/hour",
      timeline: "1-8 weeks for prototypes"
    },
    {
      icon: Bot,
      title: "AI Coding Agents & Agentic Development",
      description: "Set up and orchestrate coding agents so they deliver production-ready code grounded in your actual codebase.",
      features: [
        "Tooling setup for Claude Code, Cursor, Copilot and Codex",
        "CLAUDE.md / AGENTS.md context engineering and repo conventions",
        "Custom MCP servers, skills, hooks and slash commands",
        "Sub-agent orchestration and parallel agent workflows"
      ],
      pricing: "Starting at $130/hour",
      timeline: "1-6 weeks depending on team size and scope"
    },
    {
      icon: Fingerprint,
      title: "Strong Authentication (WebAuthn / FIDO2 / Passwordless)",
      description: "Design and implement strong, phishing-resistant authentication for your web apps.",
      features: [
        "WebAuthn/FIDO2 passkeys and security keys",
        "Passwordless login flows (platform and cross‑platform authenticators)",
        "Smart-ID, Mobile-ID, and ID-card authentication integration",
        "Identity document verification integration",
      ],
      pricing: "Starting at $140/hour",
      timeline: "1-6 weeks depending on integration scope"
    },
    {
      icon: Globe,
      title: "Domain Industry Expertise",
      description: "Specialized consulting for domain registries, registrars, and domain-related businesses.",
      features: [
        "Registry system architecture",
        "EPP protocol implementation",
        "DNS and WHOIS systems",
        "Performance optimization",
        "Security best practices"
      ],
      pricing: "Starting at $150/hour",
      timeline: "Varies by project scope"
    },
    {
      icon: FileText,
      title: "Technical Writing & Documentation",
      description: "Clear, comprehensive technical documentation and content creation for complex systems.",
      features: [
        "API documentation",
        "Technical blog posts",
        "System architecture guides",
        "Developer onboarding materials",
        "Code review and auditing",
        "Process documentation"
      ],
      pricing: "Starting at $80/hour",
      timeline: "1-4 weeks typically"
    }
  ]

  

  return (
    <>
      <Head title="Codeware - Services" >
        <meta name="description" content="Freelance development and consulting services combining Rails expertise, AI integration, and deep domain industry knowledge." />
      </Head>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Services</h1>
          <p className="text-xl text-muted-foreground mb-8">
            Freelance development and consulting services combining Rails expertise, 
            AI integration, and deep domain industry knowledge.
          </p>
          <Button size="lg" asChild>
            <Link href="/contact">Start a Project <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>

        {/* Services Grid */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.title} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{service.title}</CardTitle>
                      </div>
                    </div>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2"></h4>
                        <ul className="space-y-1">
                          {service.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {/* <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-4 border-t">
                        <div>
                          <Badge variant="outline" className="mb-1">{service.pricing}</Badge>
                          <p className="text-xs text-muted-foreground">{service.timeline}</p>
                        </div>
                      </div> */}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </section>

        {/* Process */}
        {/* <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">How We Work Together</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A streamlined process to ensure your project is delivered on time and exceeds expectations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <div key={item.step} className="text-center">
                <div className="relative mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center mx-auto">
                    {item.step}
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-6 left-full w-full h-px bg-border transform -translate-y-1/2"></div>
                  )}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </section> */}

        {/* Testimonials */}
        {/* <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Client Testimonials</h2>
            <p className="text-muted-foreground">
              What clients say about working with me
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-muted/30">
                <CardHeader>
                  <CardDescription className="text-base italic">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.company}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {testimonial.project}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section> */}

        {/* FAQ */}
        {/* <section className="mb-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What's your typical project timeline?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Project timelines vary based on complexity. Simple Rails features might take 1-2 weeks, 
                    while full applications can take 2-3 months. I provide detailed estimates after our discovery call.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Do you work with existing codebases?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Absolutely! I regularly work with existing Rails applications, providing features, 
                    performance improvements, and modernization. I can quickly assess and work with your current setup.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What's included in your Rails development service?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Full-stack development including backend APIs, database design, frontend integration, 
                    testing, deployment setup, and documentation. I follow Rails best practices and modern development workflows.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section> */}

        {/* CTA */}
        <section className="text-center bg-muted/30 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help bring your project to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link href="/contact">Get Started <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link href="/projects">View Portfolio</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  )
}

Services.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default Services